import React, { useState, useEffect } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Typography, Chip, Alert, Accordion,
  AccordionSummary, AccordionDetails, Stack, TextField,
  ToggleButtonGroup, ToggleButton, TablePagination
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import { timesheetService } from '../services/api';
import { format, startOfWeek, endOfWeek, parseISO, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { usePagination } from '../hooks/usePagination';

interface WeekGroup {
  weekStart: Date;
  weekEnd: Date;
  timesheets: any[];
  totalHours: number;
  hasAllDrafts: boolean;
  hasSubmitted: boolean;
}

// One week's timesheet entries — pulled out into its own component (rather than inline in the
// .map() below) so usePagination's hook call gets one consistent instance per week, satisfying
// the rules of hooks; each week also gets its own independent page/rowsPerPage state.
const WeekTimesheetTable: React.FC<{
  timesheets: any[];
  getStatusColor: (s: string) => any;
  onSubmit: (id: number) => void;
  onDelete: (id: number) => void;
}> = ({ timesheets, getStatusColor, onSubmit, onDelete }) => {
  const sorted = [...timesheets].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const { pagedRows, paginationProps } = usePagination(sorted);
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Project</TableCell>
            <TableCell>Task</TableCell>
            <TableCell>Hours</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pagedRows.map((ts) => (
            <TableRow key={ts.id}>
              <TableCell>{format(parseISO(ts.date), 'EEE, MMM dd')}</TableCell>
              <TableCell>{ts.projectName}</TableCell>
              <TableCell>{ts.taskDescription}</TableCell>
              <TableCell>{ts.hours}</TableCell>
              <TableCell>
                <Chip
                  label={ts.status}
                  color={getStatusColor(ts.status)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                {ts.status === 'Draft' && !ts.isLocked && (
                  <Stack direction="row" spacing={1}>
                    <Button size="small" color="primary" onClick={() => onSubmit(ts.id)}>
                      Submit
                    </Button>
                    <Button size="small" color="error" onClick={() => onDelete(ts.id)}>
                      Delete
                    </Button>
                  </Stack>
                )}
                {ts.status === 'Rejected' && (
                  <Typography variant="caption" color="error">
                    {ts.rejectionReason}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {timesheets.length > 0 && <TablePagination {...paginationProps} />}
    </TableContainer>
  );
};

const MyTimesheets: React.FC = () => {
  const [timesheets, setTimesheets] = useState<any[]>([]);
  const [weekGroups, setWeekGroups] = useState<WeekGroup[]>([]);
  const [message, setMessage] = useState('');
  
  // Filter states
  const [filterType, setFilterType] = useState<'all' | 'date' | 'month' | 'week'>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [selectedWeekStart, setSelectedWeekStart] = useState(
    format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
  );

  useEffect(() => {
    loadMyTimesheets();
  }, []);

  useEffect(() => {
    groupTimesheetsByWeek();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesheets, filterType, startDate, endDate, selectedMonth, selectedWeekStart]);

  const loadMyTimesheets = async () => {
    try {
      const data = await timesheetService.getMyTimesheets();
      setTimesheets(data);
    } catch (error) {
      console.error('Error loading timesheets:', error);
    }
  };

  const getFilteredTimesheets = () => {
    if (filterType === 'all') {
      return timesheets;
    }

    return timesheets.filter((ts) => {
      const date = parseISO(ts.date);

      switch (filterType) {
        case 'date':
          if (startDate && endDate) {
            const start = parseISO(startDate);
            const end = parseISO(endDate);
            // Validate date range to avoid RangeError
            if (start > end) {
              return true; // Show all if invalid range
            }
            return isWithinInterval(date, { start, end });
          }
          return true;

        case 'month':
          const monthStart = startOfMonth(parseISO(selectedMonth + '-01'));
          const monthEnd = endOfMonth(monthStart);
          return isWithinInterval(date, { start: monthStart, end: monthEnd });

        case 'week':
          // Normalize to Monday-Sunday week
          const weekStart = startOfWeek(parseISO(selectedWeekStart), { weekStartsOn: 1 });
          const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
          return isWithinInterval(date, { start: weekStart, end: weekEnd });

        default:
          return true;
      }
    });
  };

  const groupTimesheetsByWeek = () => {
    const filteredTimesheets = getFilteredTimesheets();
    
    if (filteredTimesheets.length === 0) {
      setWeekGroups([]);
      return;
    }

    const groups: Map<string, WeekGroup> = new Map();

    filteredTimesheets.forEach((ts) => {
      const date = parseISO(ts.date);
      const weekStart = startOfWeek(date, { weekStartsOn: 1 }); // Monday
      const weekEnd = endOfWeek(date, { weekStartsOn: 1 }); // Sunday
      const weekKey = format(weekStart, 'yyyy-MM-dd');

      if (!groups.has(weekKey)) {
        groups.set(weekKey, {
          weekStart,
          weekEnd,
          timesheets: [],
          totalHours: 0,
          hasAllDrafts: true,
          hasSubmitted: false
        });
      }

      const group = groups.get(weekKey)!;
      group.timesheets.push(ts);
      group.totalHours += ts.hours;
      
      if (ts.status !== 'Draft') {
        group.hasAllDrafts = false;
      }
      if (ts.status === 'Submitted' || ts.status === 'Approved') {
        group.hasSubmitted = true;
      }
    });

    // Sort by week start date (most recent first)
    const sortedGroups = Array.from(groups.values()).sort(
      (a, b) => b.weekStart.getTime() - a.weekStart.getTime()
    );

    setWeekGroups(sortedGroups);
  };

  const handleSubmit = async (id: number) => {
    try {
      await timesheetService.submitTimesheet(id);
      setMessage('Timesheet submitted successfully');
      loadMyTimesheets();
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error submitting timesheet');
    }
  };

  const handleSubmitWeek = async (week: WeekGroup) => {
    const draftTimesheets = week.timesheets.filter(ts => ts.status === 'Draft' && !ts.isLocked);
    
    if (draftTimesheets.length === 0) {
      setMessage('No draft timesheets to submit for this week');
      return;
    }

    try {
      // Submit all draft timesheets in the week
      await Promise.all(draftTimesheets.map(ts => timesheetService.submitTimesheet(ts.id)));
      setMessage(`Successfully submitted ${draftTimesheets.length} timesheet(s) for the week`);
      loadMyTimesheets();
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error submitting week timesheets');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this timesheet?')) return;
    try {
      await timesheetService.deleteTimesheet(id);
      setMessage('Timesheet deleted successfully');
      loadMyTimesheets();
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error deleting timesheet');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Draft': return 'default';
      case 'Submitted': return 'warning';
      case 'Approved': return 'success';
      case 'Rejected': return 'error';
      default: return 'default';
    }
  };

  const getWeekStatusChip = (week: WeekGroup) => {
    const allApproved = week.timesheets.every(ts => ts.status === 'Approved');
    const allSubmitted = week.timesheets.every(ts => ts.status === 'Submitted' || ts.status === 'Approved');
    const hasRejected = week.timesheets.some(ts => ts.status === 'Rejected');
    
    if (allApproved) {
      return <Chip label="All Approved" color="success" size="small" />;
    } else if (hasRejected) {
      return <Chip label="Has Rejected" color="error" size="small" />;
    } else if (allSubmitted) {
      return <Chip label="All Submitted" color="warning" size="small" />;
    } else if (week.hasSubmitted) {
      return <Chip label="Partially Submitted" color="info" size="small" />;
    } else {
      return <Chip label="Draft" color="default" size="small" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        My Timesheets (Grouped by Week)
      </Typography>

      {message && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      {/* Filter Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon color="action" />
            <Typography variant="subtitle2">Filter Timesheets</Typography>
          </Box>
          
          <ToggleButtonGroup
            value={filterType}
            exclusive
            onChange={(e, value) => value && setFilterType(value)}
            size="small"
            fullWidth
          >
            <ToggleButton value="all">All Timesheets</ToggleButton>
            <ToggleButton value="date">Date Range</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
          </ToggleButtonGroup>

          {filterType === 'date' && (
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Stack>
          )}

          {filterType === 'month' && (
            <TextField
              fullWidth
              label="Select Month"
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          )}

          {filterType === 'week' && (
            <TextField
              fullWidth
              label="Week Start (Monday)"
              type="date"
              value={selectedWeekStart}
              onChange={(e) => setSelectedWeekStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Select any date - the week will start from that Monday"
              size="small"
            />
          )}

          <Typography variant="caption" color="text.secondary">
            {filterType === 'all' && `Showing all ${timesheets.length} timesheets`}
            {filterType === 'date' && startDate && endDate && (() => {
              const start = parseISO(startDate);
              const end = parseISO(endDate);
              if (start > end) {
                return 'Invalid date range - end date must be after start date';
              }
              return `Showing timesheets from ${format(start, 'MMM dd, yyyy')} to ${format(end, 'MMM dd, yyyy')}`;
            })()}
            {filterType === 'month' && `Showing timesheets for ${format(parseISO(selectedMonth + '-01'), 'MMMM yyyy')}`}
            {filterType === 'week' && `Showing timesheets for week ${format(startOfWeek(parseISO(selectedWeekStart), { weekStartsOn: 1 }), 'MMM dd')} - ${format(endOfWeek(parseISO(selectedWeekStart), { weekStartsOn: 1 }), 'MMM dd, yyyy')}`}
          </Typography>
        </Stack>
      </Paper>

      {weekGroups.length === 0 && (
        <Typography color="text.secondary">No timesheets found. Start logging your time!</Typography>
      )}

      {weekGroups.map((week, index) => (
        <Accordion key={index} defaultExpanded={index === 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
              <Typography sx={{ flexGrow: 1 }}>
                <strong>Week of {format(week.weekStart, 'MMM dd, yyyy')}</strong> to {format(week.weekEnd, 'MMM dd, yyyy')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {week.timesheets.length} entries | {week.totalHours} hours
              </Typography>
              {getWeekStatusChip(week)}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {week.timesheets.filter(ts => ts.status === 'Draft').length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleSubmitWeek(week)}
                  >
                    Submit All Drafts for This Week ({week.timesheets.filter(ts => ts.status === 'Draft').length})
                  </Button>
                </Box>
              )}
              
              <WeekTimesheetTable
                timesheets={week.timesheets}
                getStatusColor={getStatusColor}
                onSubmit={handleSubmit}
                onDelete={handleDelete}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default MyTimesheets;
